import React from 'react';
import './GameEndPlayerInfo.css';
import {getPlaceIconPath} from "../../../utils/gameEnd/getPlaceIconPath";
import {getPlayerHexColor} from "../../../utils/players/getPlayerHexColor";
import {WinPointsStat} from "../../PlayerStat/stats/WinPointsStat";
import {CoinsStat} from "../../PlayerStat/stats/CoinsStat";
import {resolveFactionSkinIconPath} from "../../../utils/factions/resolveFactionSkinIconPath";
import RankSkinFrame from '../../RankSkinFrame/RankSkinFrame';
import VisibleBotMark from '../../VisibleBotMark/VisibleBotMark';
import {getBotMarkIconPath} from '../../../utils/bots/getBotMarkIconPath';
import {checkIfBotMarkShown} from '../../../utils/bots/checkIfBotMarkShown';
import {getVisibleBotTitle} from '../../../utils/bots/getVisibleBotTitle';
import {getBotMarkAlt} from '../../../utils/bots/getBotMarkAlt';
import {MINUS_SIGN} from "../../../constants/text";
import {getLeagueByRating} from "../../../utils/rating/getLeagueByRating";
import {getChestIconPath} from "../../../utils/inventory/getChestIconPath";
import {countChestsByType} from "../../../utils/inventory/countChestsByType";
import {getChestDisplayName} from "../../../utils/inventory/getChestDisplayName";
import {getFactionRankIconPath} from "../../../utils/factions/getFactionRankIconPath";
import {getFactionRankSkinTitle, parseRankSkinId} from "../../../utils/factions/getFactionRankSkinTitle";
import {getPolicyImagePath} from "../../../utils/policy/getPolicyImagePath";
import {getFactionConfig} from "../../../utils/factions/getFactionConfig";
import {getFactionIconPath} from "../../../utils/factions/getFactionIconPath";
import {MASKED_ICON_TONES, MaskedIcon} from '../../coloredIcons/MaskedIcon/MaskedIcon';

const GameEndPlayerInfo = (
  {
    player,
    index,
    // Реальное место (с 1): у полной ничьей оно общее, а следующий за ней
    // игрок получает место с учетом всех опередивших (1, 2, 2, 4). Не передано
    // — место по порядку в списке
    place: placeProp,
    isRanked,
    isCompactMode,
    isBlackOutline,
    customClassName,
    forceSeparateRewardsRow,
    currentUserId,
    onClick,
    coins,
    showPolicy,
    noPlaceIcon = false,
    // Итоговых очков нет (партия не доиграна): без этого у каждого игрока
    // стоял бы «0», как будто партия кончилась с нулевым счетом
    noFinalWinPoints = false,
    // Промежуточные итоги раунда (см. RoundEndModal): текущие очки без
    // конвертации монет, сдвиг места с прошлого раунда (> 0 — поднялся) и
    // прирост итоговых очков за раунд. Не переданы — не показываются
    winPoints,
    placeChange,
    finalWinPointsDelta,
    // Значок бота в углу иконки фракции, как в списке игроков на боковой
    // панели, а не иконкой рядом с ником. Как и там, помечаются только боты,
    // чья пометка публична (см. checkIfBotMarkShown)
    isBotMarkOnIcon = false,
  }
) => {
  const {
    color,
    name,
    factionId,
    selectedSkin,
    finalWinPoints,
    ratingDelta,
    rating,
    numens,
    chests,
    skins,
    isBot,
    isVisibleBot,
    policy,
    isDoubleReward,
    isPremiumReward,
  } = player;

  const playerHexColor = getPlayerHexColor(color);
  // Явный бот рейтингового матча наград не получает, и рейтинг ему не меняют
  // (см. finalizeGame) — значит и показывать в итогах нечего. Гасим здесь, а не
  // только на сервере: этот же компонент рисует детали сыгранного матча и
  // реплей, где числа приезжают из записи матча
  const hasRatingInfo = !isVisibleBot && typeof ratingDelta === 'number' && typeof rating === 'number';
  const hasNumensInfo = !isVisibleBot && typeof numens === 'number';
  const hasChestsInfo = !isVisibleBot && Array.isArray(chests) && chests.length > 0;
  const hasSkinsInfo = !isVisibleBot && Array.isArray(skins) && skins.length > 0;
  const chestCounts = countChestsByType(chests);
  const league = getLeagueByRating(rating);

  const renderRating = () => (
    <>
      <span className="game-end-player-rating-label">Рейтинг:</span>
      <span className="game-end-player-rating-delta">
        {ratingDelta >= 0 ? `+${ratingDelta}` : `${MINUS_SIGN}${Math.abs(ratingDelta)}`}
      </span>
      <div className="game-end_rating-from-to">
        <MaskedIcon
          src={'/img/icons/up.png'}
          alt={'right arrow'}
          tone={MASKED_ICON_TONES.CONTROLS}
        />
      </div>
      <span className="game-end-player-rating-value">{rating}</span>
      <img
        className="game-end-player-league-icon"
        src={league.iconPath}
        alt={league.name}
        title={league.name}
      />
    </>
  );

  const renderNumens = () => (
    <>
      <span className="game-end-player-numens-label text-coins-dark">Награды:</span>
      <span className="game-end-player-numens-value text-coins-dark">
        {numens}
        <img
          className="game-end-player-numens-icon"
          src="/img/icons/numens.png"
          alt="Нумены"
        />
      </span>

    </>
  );

  const renderChests = () => (
    <div className="game-end-player-chests">
      {chestCounts.map(({type, count}) => (
        <div key={type} className="game-end-player-chest">
          <img
            className="game-end-player-chest-icon"
            src={getChestIconPath(type)}
            alt={getChestDisplayName(type)}
            title={getChestDisplayName(type)}
          />
          {count > 1 && (
            <span className="game-end-player-chest-count">×{count}</span>
          )}
        </div>
      ))}
    </div>
  );

  const renderSkins = () => (
    <div className="game-end-player-skins">
      {skins.map((skinId, index) => {
        const {factionId, rank} = parseRankSkinId(skinId);
        // Подсказка по-русски: идентификатор образа (`humans_gold`) игроку
        // ничего не говорит
        const skinTitle = getFactionRankSkinTitle(factionId, rank);
        return (
          <img
            key={`${skinId}-${index}`}
            className="game-end-player-skin-icon"
            src={getFactionRankIconPath(factionId, rank)}
            alt={skinTitle}
            title={skinTitle}
          />
        );
      })}
    </div>
  );

  // Награды за главы обучения: дни премиума и открытая фракция (пишутся в
  // запись матча-обучения, см. /users/me/tutorial-chapters/complete)
  const {premiumRewardDays, factionRewardId} = player;
  const hasPremiumRewardInfo = typeof premiumRewardDays === 'number' && premiumRewardDays > 0;
  const hasFactionRewardInfo = Boolean(factionRewardId);

  const renderPremiumReward = () => (
    <div className="game-end-player-tutorial-reward">
      <img
        className="game-end-player-tutorial-reward-icon"
        src="/img/icons/premium.png"
        alt="Премиум"
        title={`Премиум-аккаунт на ${premiumRewardDays} дн.`}
      />
      <span className="game-end-player-tutorial-reward-label text-coins-dark">
        {`Премиум ${premiumRewardDays} дн.`}
      </span>
    </div>
  );

  const renderFactionReward = () => {
    const factionRewardConfig = getFactionConfig(factionRewardId);
    return (
      <div className="game-end-player-tutorial-reward">
        <img
          className="game-end-player-tutorial-reward-icon"
          src={getFactionIconPath(factionRewardId)}
          alt={factionRewardConfig?.name || factionRewardId}
          title={`Фракция: ${factionRewardConfig?.name || factionRewardId}`}
        />
        <span className="game-end-player-tutorial-reward-label text-coins-dark">
          {factionRewardConfig?.name || factionRewardId}
        </span>
      </div>
    );
  };

  const renderRewardsGroup = () => (
    <div className="game-end-player-rewards-group">
      {hasNumensInfo && renderNumens()}
      {hasChestsInfo && renderChests()}
      {hasSkinsInfo && renderSkins()}
      {hasPremiumRewardInfo && renderPremiumReward()}
      {hasFactionRewardInfo && renderFactionReward()}
    </div>
  );

  // Бейдж двойной награды (первый рейтинговый матч игрового дня) —
  // под наградами, виден всем игрокам, не только получателю
  // Один бейдж на все комбинации множителей наград:
  //  - х2 за первый рейтинговый матч дня (без премиума);
  //  - х2 за премиум (не первый матч дня) — с иконкой премиума;
  //  - х4 = х2 * х2 (премиум В первый матч дня) — единый лейбл с иконкой,
  //    а не два отдельных
  const renderRewardMultiplierBadge = () => {
    const badgeText = isDoubleReward
      ? `Награда х${isPremiumReward ? 4 : 2} за первый матч дня`
      : 'Награда х2';
    return (
      <div className="game-end-player-double-reward-row">
        <span
          className={`game-end-player-double-reward-badge ${
            isPremiumReward ? 'game-end-player-double-reward-badge_premium' : ''
          }`}
        >
          {badgeText}
        </span>
      </div>
    );
  };

  const isCurrentUser = currentUserId && player.id === currentUserId;
  const place = placeProp ?? index + 1;

  return (
    <div
      className={`game-end-player-info ${customClassName || ''} ${place === 1 ? 'game-end-player-info-first' : ''} ${isCurrentUser ? 'game-end-player-info_current-user' : ''} ${onClick ? 'game-end-player-info_clickable' : ''}`}
      key={player.id}
      style={{borderLeftColor: playerHexColor}}
      onClick={onClick}
    >
      <div className="game-end-player-main-row">
        {!noPlaceIcon && (
          <div className="game-end-player-place">
            {getPlaceIconPath(place - 1) && (
              <img
                src={getPlaceIconPath(place - 1)}
                alt={`${place} место`}
                className="place-icon"
              />
            )}
          </div>
        )}

        <div className="game-end-player-icon">
          <img
            className={'side-menu_faction-icon'}
            src={resolveFactionSkinIconPath(factionId, selectedSkin)}
            alt={factionId}
          />
          <RankSkinFrame skinId={selectedSkin}/>
          {isBotMarkOnIcon && checkIfBotMarkShown(player) && <VisibleBotMark player={player}/>}
          {typeof placeChange === 'number' && placeChange !== 0 && (
            // Сдвиг места с прошлого раунда — сдвоенным шевроном в углу
            // портрета: вверх — в левом верхнем, вниз — в левом нижнем
            <span
              className={`game-end-player-place-change ${placeChange > 0
                ? 'game-end-player-place-change_up'
                : 'game-end-player-place-change_down'}`}
              title={placeChange > 0
                ? `+${placeChange} с прошлого раунда`
                : `${MINUS_SIGN}${Math.abs(placeChange)} с прошлого раунда`}
            >
              {[0, 1].map((key) => (
                <MaskedIcon
                  key={key}
                  className="game-end-player-place-change-chevron"
                  src={placeChange > 0 ? '/img/icons/arrow up.png' : '/img/icons/arrow down.png'}
                />
              ))}
            </span>
          )}
        </div>

        <div
          className="game-end-player-name"
          style={{color: playerHexColor}}
        >
          <span className="game-end-player-name-text" title={name}>{name}</span>
          {!isBotMarkOnIcon && (isBot || checkIfBotMarkShown(player)) && (
            <img
              className="game-end-player-bot-icon"
              // У помеченного бота иконка своя: у «явного» — по лиге, у бота
              // локальной партии — по выбранному бонусу. Та же, что у значка на
              // иконке фракции. Бот сюжетной миссии не помечен ничем, хотя
              // бонус в его конфиге быть может, — ему общая серая иконка
              src={checkIfBotMarkShown(player)
                ? getBotMarkIconPath(player, isBlackOutline)
                : '/img/icons/bot.png'}
              // У ИИ-игрока промо-матча — «ИИ», он не бот (иконка у него своя)
              alt={getBotMarkAlt(player)}
              // У помеченного бота в подсказке лига (или уровень бонуса) и его
              // расшифровка — теми же строками, что и подсказка выбора бонуса
              // в локальной игре
              title={checkIfBotMarkShown(player) ? getVisibleBotTitle(player) : 'Бот'}
            />
          )}
        </div>

        {typeof winPoints === 'number' ? (
          // Итоги раунда: текущие монеты и очки столбиком, а стрелка за ним —
          // «превращаются в» итоговые очки справа
          <>
            <div className="game-end-player-current-stats">
              <CoinsStat
                className={'result-window_coins-text'}
                text={coins}
                isDarkColor={true}
                noShrinkOnMobile={true}
              />
              <WinPointsStat
                className={'result-window_current-win-points-text'}
                text={winPoints}
                isDarkColor={true}
                noShrinkOnMobile={true}
              />
            </div>
            <MaskedIcon
              className="game-end-player-conversion-arrow"
              src={'/img/icons/arrow right.png'}
              color={'var(--text-secondary-color)'}
              title={'Монеты переводятся в очки — столько очков было бы, закончись игра сейчас'}
            />
          </>
        ) : typeof coins === 'number' && (
          <CoinsStat
            className={'result-window_coins-text'}
            text={coins}
            isDarkColor={true}
            noShrinkOnMobile={true}
          />
        )}

        {!noFinalWinPoints && (
          <div className="game-end-player-final-win-points">
            <WinPointsStat
              className={'result-window_win-points-text'}
              text={finalWinPoints}
              isDarkColor={true}
              noShrinkOnMobile={true}
            />
            {typeof finalWinPointsDelta === 'number' && (
              <span
                className="game-end-player-final-win-points-delta"
                title={'Изменение итоговых очков с прошлого раунда'}
              >
                {finalWinPointsDelta > 0 && `+${finalWinPointsDelta}`}
                {finalWinPointsDelta < 0 && `${MINUS_SIGN}${Math.abs(finalWinPointsDelta)}`}
                {finalWinPointsDelta === 0 && '0'}
              </span>
            )}
          </div>
        )}
      </div>

      {showPolicy && (
        <div className="game-end-player-policy-row">
          {policy && (
            <img
              className="game-end-player-policy-icon"
              src={getPolicyImagePath(policy.id)}
              alt={policy.name}
            />
          )}
          {policy?.name || 'Нет политики'}
        </div>
      )}

      {!isCompactMode && (hasRatingInfo || hasNumensInfo || hasChestsInfo || hasSkinsInfo
        || hasPremiumRewardInfo || hasFactionRewardInfo) && (
        forceSeparateRewardsRow ? (
          <>
            {isRanked && hasRatingInfo && (
              <div className="game-end-player-rewards-row game-end-player-rewards-row_ranked">
                <div className="game-end-player-rewards-group text-rating">
                  {renderRating()}
                </div>
              </div>
            )}
            {(hasNumensInfo || hasChestsInfo || hasSkinsInfo || hasPremiumRewardInfo || hasFactionRewardInfo) && (
              <div className="game-end-player-rewards-row game-end-player-rewards-row_casual">
                {renderRewardsGroup()}
              </div>
            )}
          </>
        ) : (
          <div
            className={`game-end-player-rewards-row ${isRanked ? 'game-end-player-rewards-row_ranked' : 'game-end-player-rewards-row_casual'}`}
          >
            {isRanked && hasRatingInfo && (
              <div className="game-end-player-rewards-group text-rating">
                {renderRating()}
              </div>
            )}
            {(hasNumensInfo || hasChestsInfo || hasSkinsInfo || hasPremiumRewardInfo || hasFactionRewardInfo) && (
              renderRewardsGroup()
            )}
          </div>
        )
      )}

      {/* Бейдж множителя — часть наградного блока, поэтому гаснет по тому же
          признаку: у явного бота наград нет. Гасится и здесь, а не только на
          сервере, ради уже записанных матчей */}
      {!isCompactMode && !isVisibleBot && (isDoubleReward || isPremiumReward) && renderRewardMultiplierBadge()}
    </div>
  );
};

export default GameEndPlayerInfo;
